import { events } from "@constants/tracking";
import { calculatePlanExpiry } from "@memorang/helpers/bundle";
import type { Plan } from "@memorang/types";
import { trackCustomEvent } from "analytics";
import { createCheckoutSession } from "api/commerce/product-service";
import { t } from "i18next";
import pluralize from "pluralize";

type Params = {
	selectedPlan: Plan;
	productId: string;
	redirectUrl: string;
	productName: string;
	productDescription: string;
	userId: string;
};
export const handleCreateCheckoutSession = async ({
	selectedPlan,
	productId,
	redirectUrl,
	productName,
	productDescription,
	userId,
}: Params): Promise<string> => {
	const {
		id: planId,
		amount,
		duration,
		interval,
		currency,
		name,
	} = selectedPlan;
	const handleTracking = (
		eventName: string,
		params?: Record<string, string | number>,
	) => {
		trackCustomEvent({
			eventName,
			bundleId: productId,
			bundleName: productName,
			...params,
		});
	};
	const checkoutParams = {
		productId,
		planId,
		description: productDescription,
		purchaseMode: "USER",
		successUrl: redirectUrl,
		failureUrl: redirectUrl,
		checkoutMode: "EMBED",
		quantity: 1,
		cancelUrl: redirectUrl,
	};

	const trackParams = {
		plan_id: planId,
		plan_duration: `${duration} ${interval}`,
		plan_price: amount,
		plan_currency: currency,
		plan_name: name,
	};

	handleTracking(events.paymentSubmitted, trackParams);
	const result = await createCheckoutSession({
		userId,
		payment: checkoutParams,
	});

	return result.clientSecret;
};

export const getAccessDurationTemplate = (
	plan?: Plan,
	expiryDate?: string,
	yearPlan?: Plan,
) => {
	if (!plan) {
		return "";
	}
	const isYearPlan = plan.duration === 12;

	if (isYearPlan) {
		return t("purchase.paywall.accesDuration.yearPlan", {
			duration: plan.duration,
			interval: t(`common.${plan.interval}`),
		});
	}

	const expiryText = calculatePlanExpiry(
		plan.interval,
		plan.duration,
		expiryDate,
	);

	return t("purchase.paywall.accesDuration.otherPlan", {
		duration: plan.duration,
		interval: pluralize(t(`common.${plan.interval}`), plan.duration),
		expiryText,
		moreTime: yearPlan
			? t("purchase.paywall.accesDuration.moreTime", {
					yearPlanDuration: yearPlan?.duration,
					yearPlanInterval: t(`common.${yearPlan?.interval}`),
				})
			: "",
	});
};

export const getSubscriptionIntervalText = (
	interval?: string,
	duration?: number,
	capitalize = true,
) => {
	if (interval === "month") {
		if (duration === 12) {
			return capitalize ? "Yearly" : "yearly";
		}
		return capitalize ? "Monthly" : "monthly";
	}

	if (interval === "week") {
		return capitalize ? "Weekly" : "weekly";
	}
};
